import { css } from '@emotion/react';
import {
  faFilterList, faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { HoverableIconComponent } from '~/_shared/components/icons/hoverableIcon.component';
import { SPREADSHEET_CELL_HEIGHT } from '~/_shared/components/spreadsheet/cell/spreadsheetCell.component';
import type { SpreadsheetRow } from '~/_shared/components/spreadsheet/spreadsheet.types';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { Theme } from '~/_shared/themes/theme.model';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import { useTranslation } from '~/_shared/utils/hooks';
import { useDebounce } from '~/_shared/utils/hooks/useDebounce';
import { useElementDimensions } from '~/_shared/utils/hooks/useElementDimensions';
import { getPolygonPathFromLatLngBounds } from '~/_shared/utils/map/latLngBounds.factory';
import {
  dataTableWrapperStyle,
  overridePaddingStyle, splitScreenDataTableWrapperStyle,
} from '~/data/table/dataTableWrapper.styles';
import { clearSpreadsheetPreviewFilterState } from '~/store/frontendState/activeMapElements/activeMapElements.actionCreators';
import { useActiveMarkerIdSelector } from '~/store/frontendState/activeMapElements/activeMapElements.selectors';
import { useSpreadsheetPreviewFilters } from '~/store/frontendState/activeMapElements/useSpreadsheetPreviewFilters.hook';
import { useMapComponentLastBoundsSelector } from '~/store/frontendState/mapComponent/mapComponent.selectors';
import { rightPanelHoverMarker } from '~/store/frontendState/mapTools/locationFinder/locationFinder.actionCreators';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import { createAreaRequestGetterFilter } from '~/store/spreadsheetData/area/spreadsheetData.area.helpers';
import { mergeFilterTrees } from '~/store/spreadsheetData/filtering/spreadsheetDataFiltering.helpers';
import { useFilterTreeMapSettingsParams } from '~/store/spreadsheetData/filtering/useFilterTreeMapSettingsParams';
import {
  DataTableComponent, META_CONTAINER_HEIGHT,
} from './dataTable.component';
import { useManageDataTableSpreadsheet } from './useManageDataTableSpreadsheet.hook';

const previewFiltersBarStyle = (theme: Theme) => css({
  alignItems: 'center',
  color: theme.textColors.info,
  display: 'flex',
  fontSize: 13,
  gap: 6,
  padding: '0 0 8px',
});

const previewFiltersLabelStyle = css({
  whiteSpace: 'nowrap',
});

const previewFiltersListStyle = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const DataTableSplitScreenContainer: FC = () => {
  const [t] = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const spreadsheetId = usePrimarySpreadsheetId();
  const mapSettingsFilterTree = useFilterTreeMapSettingsParams();
  const lastBounds = useMapComponentLastBoundsSelector()?.bounds ?? null;
  const activeMarkerId = useActiveMarkerIdSelector();
  const spreadsheetPreviewFilters = useSpreadsheetPreviewFilters();

  const [currentBounds, setCurrentBounds] = useState(lastBounds);

  const setCurrentBoundsDebounced = useDebounce(setCurrentBounds, 500);

  const lastBoundsFilter = useMemo((): MultiPolygon[] | undefined => {
    if (currentBounds && spreadsheetPreviewFilters.isEmpty) {
      const paths = getPolygonPathFromLatLngBounds(currentBounds);
      return [paths.map(p => ({ path: p, holes: [] }))];
    }
    return undefined;
  }, [currentBounds, spreadsheetPreviewFilters.isEmpty]);

  const filter = useMemo(() => {
    if (!spreadsheetId) {
      return undefined;
    }

    const baseFilter = createAreaRequestGetterFilter({
      filtersCombine: 'and',
      filterTreeParams: mapSettingsFilterTree,
      spreadsheetId,
      multiPolygons: lastBoundsFilter,
    });

    const previewFilter = createAreaRequestGetterFilter({
      filtersCombine: 'or',
      spreadsheetId,
      multiPolygons: spreadsheetPreviewFilters.multiPolygons,
      circles: spreadsheetPreviewFilters.circles,
      boundaries: spreadsheetPreviewFilters.boundaries,
      boundaryTerritories: spreadsheetPreviewFilters.boundaryTerritories,
    });

    return {
      ...baseFilter,
      ...previewFilter,
      filter_tree: mergeFilterTrees([baseFilter.filter_tree, previewFilter.filter_tree], 'and') || undefined,
    };
  }, [spreadsheetId, mapSettingsFilterTree, lastBoundsFilter, spreadsheetPreviewFilters]);

  const originalDataTableSpreadsheet = useManageDataTableSpreadsheet({
    debounceGetSpreadsheetData: true,
    filter,
    initialPerPage: 10,
    spreadsheetId,
    activeMarkerId,
  });

  const highlightedRows = useMemo(() => activeMarkerId ? new Set([activeMarkerId.rowId]) : undefined, [activeMarkerId]);

  const manageDataTableSpreadsheet = {
    ...originalDataTableSpreadsheet,
    paginationProps: {
      ...originalDataTableSpreadsheet.paginationProps,
      perPageOptions: undefined,
    },
  };

  const onHoveredRowChange = useCallback((row: SpreadsheetRow | null) => {
    dispatch(rightPanelHoverMarker(
      row
        ? {
          rowId: row.rowId,
          spreadsheetId: row.virtualSpreadsheetId,
        }
        : null
    ));
  }, [dispatch]);

  const onClearPreviewFilters = useCallback(() => {
    dispatch(clearSpreadsheetPreviewFilterState());
  }, [dispatch]);

  useEffect(() => {
    setCurrentBoundsDebounced(lastBounds);
  }, [setCurrentBoundsDebounced, lastBounds]);

  const { ref: wrapperRef, height: wrapperHeight } = useElementDimensions();
  const { ref: metaRef, height: metaHeight } = useElementDimensions();
  const { ref: previewFiltersRef, height: previewFiltersHeight } = useElementDimensions();

  const { paginationProps: { onPerPageChange, pagination: { perPage } } } = manageDataTableSpreadsheet;

  useEffect(() => {
    if (wrapperHeight) {
      /**
       * Available height = wrapperHeight - Pagination element height - preview filters bar height - top and bottom
       * paddings (when pagination is present) We need to measure the outside because adding or removing the
       * pagination changes the dimensions of the inside => causes another request.
       * We always calculate the size as if pagination was there: Either we can
       * fit all the rows and there's a bit of extra space which is normal since we list all the items or the
       * pagination is indeed there.
       */
      const availableHeight = wrapperHeight - META_CONTAINER_HEIGHT - (previewFiltersHeight ?? 0)
        - (splitScreenDataTableWrapperStyle.padding + overridePaddingStyle.paddingBottom);
      const newPerPage = Math.floor((availableHeight - SPREADSHEET_CELL_HEIGHT) / SPREADSHEET_CELL_HEIGHT)
        - (activeMarkerId ? 1 : 0);
      if (newPerPage !== perPage && newPerPage > 0) {
        onPerPageChange(newPerPage);
      }
    }
  }, [onPerPageChange, wrapperHeight, previewFiltersHeight, perPage, activeMarkerId]);

  return (
    <div
      ref={wrapperRef}
      css={[dataTableWrapperStyle, splitScreenDataTableWrapperStyle, metaHeight ? overridePaddingStyle : undefined]}
    >
      {spreadsheetPreviewFilters.names.length > 0 && (
        <div ref={previewFiltersRef} css={previewFiltersBarStyle}>
          <FontAwesomeIcon icon={faFilterList} />
          <span css={previewFiltersLabelStyle}>{t('Filtered By:')}</span>
          <span css={previewFiltersListStyle}>
            {spreadsheetPreviewFilters.names.join(', ')}
          </span>
          <TooltipComponent tooltipContent={t('Clear')}>
            <HoverableIconComponent
              baseColor={theme.iconColors.secondary}
              hoverColor={theme.iconColors.secondaryHover}
              icon={faXmark}
              title={t('Clear')}
              onClick={onClearPreviewFilters}
            />
          </TooltipComponent>
        </div>
      )}

      <DataTableComponent
        highlightedRows={highlightedRows}
        isLoading={manageDataTableSpreadsheet.isLoading}
        isSearchFilterActive={false}
        metaRef={metaRef}
        onHoveredRowChange={onHoveredRowChange}
        showBadDataSection={false}
        spreadsheetProps={manageDataTableSpreadsheet}
      />
    </div>
  );
};
